<template>
  <div class="add-post-view">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-6">
      <div class="d-flex align-center ga-3">
        <v-btn
          to="/movies/homeview"
          icon
          variant="tonal"
          size="small"
          aria-label="Back"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h4 font-weight-bold">Add Movie</h1>
          <p class="text-caption text-medium-emphasis mb-0">Add a new film to your personal cinema vault</p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex align-center flex-wrap ga-2">
        <v-btn
          variant="tonal"
          size="default"
          class="btn-modern"
          :disabled="saving || savingWishList"
          :loading="savingWishList"
          prepend-icon="mdi-bookmark-plus-outline"
          @click="saveWishList"
        >
          Save to Wishlist
        </v-btn>

        <v-btn
          color="primary"
          size="default"
          class="btn-modern btn-glow-primary"
          :disabled="saving || savingWishList"
          :loading="saving"
          prepend-icon="mdi-check"
          @click="saveMovie"
        >
          Save to Collection
        </v-btn>
      </div>
    </div>

    <!-- Alert Messages -->
    <v-alert v-if="error" type="error" class="mb-4" closable variant="tonal" @click:close="error = ''">
      {{ error }}
    </v-alert>

    <v-alert v-if="autofillMessage" type="info" class="mb-4" closable variant="tonal" @click:close="autofillMessage = ''">
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-information-outline</v-icon>
        {{ autofillMessage }}
      </div>
    </v-alert>

    <!-- TMDB Smart Autofill Search Bar -->
    <v-card class="glass-card pa-4 mb-6 autofill-card">
      <div class="d-flex align-center ga-2 mb-2">
        <v-icon color="primary" size="20">mdi-auto-fix</v-icon>
        <span class="text-subtitle-2 font-weight-bold">Smart TMDB Auto-Fill</span>
      </div>
      <v-row dense align="center">
        <v-col cols="12" sm="8" md="9">
          <v-text-field
            v-model="movie.title"
            placeholder="Type movie title (e.g. Inception, Interstellar, Dune)..."
            density="compact"
            hide-details
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            @keyup.enter="autofill"
          />
        </v-col>
        <v-col cols="12" sm="4" md="3" class="d-flex ga-2">
          <v-btn
            color="primary"
            variant="flat"
            block
            class="btn-modern"
            :loading="searchingTmdb"
            :disabled="!movie.title"
            prepend-icon="mdi-sparkles"
            @click="autofill"
          >
            Auto-Fill
          </v-btn>
        </v-col>
      </v-row>

      <!-- Search Matches Dialog / Preview -->
      <div v-if="searchResults.length > 1" class="mt-3 pt-3 border-t-subtle d-flex align-center justify-space-between flex-wrap ga-2">
        <span class="text-caption text-medium-emphasis">
          Match {{ movieInstance + 1 }} of {{ searchResults.length }}: <strong>{{ searchResults[movieInstance]?.title }}</strong> ({{ (searchResults[movieInstance]?.release_date || '').substring(0, 4) }})
        </span>
        <v-btn
          size="x-small"
          variant="tonal"
          class="btn-modern"
          prepend-icon="mdi-skip-next"
          @click="autofillNext"
        >
          Try Next Match
        </v-btn>
      </div>
    </v-card>

    <!-- Main Form Grid -->
    <v-form ref="formRef">
      <v-row>
        <!-- Left Column: Poster Upload & Preview -->
        <v-col cols="12" md="4" lg="3">
          <v-card class="glass-card pa-4 text-center sticky-poster-card">
            <div class="text-subtitle-2 font-weight-bold mb-3 text-left">Movie Poster</div>
            
            <div class="poster-dropzone mb-3">
              <v-img
                v-if="posterPreview"
                :src="posterPreview"
                aspect-ratio="2/3"
                cover
                class="rounded-lg shadow-md"
              />
              <div v-else class="poster-empty-placeholder">
                <v-icon size="48" color="primary" class="mb-2">mdi-image-plus-outline</v-icon>
                <div class="text-caption font-weight-bold">No Poster Selected</div>
                <div class="text-caption text-medium-emphasis">Upload an image or use auto-fill</div>
              </div>
            </div>

            <v-file-input
              v-model="imageFile"
              label="Upload Custom Poster"
              accept="image/*"
              density="compact"
              hide-details
              prepend-icon=""
              prepend-inner-icon="mdi-upload"
              variant="outlined"
              class="mb-2"
            />

            <v-text-field
              v-model="movie.imageurl"
              label="Or Poster Image URL"
              placeholder="https://..."
              density="compact"
              hide-details
              variant="outlined"
            />
          </v-card>
        </v-col>

        <!-- Right Column: Form Sections -->
        <v-col cols="12" md="8" lg="9">
          <!-- Section 1: Core Information -->
          <v-card class="glass-card pa-6 mb-6">
            <div class="d-flex align-center ga-2 mb-4">
              <v-icon color="primary" size="22">mdi-information-outline</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold mb-0">Core Information</h3>
            </div>

            <v-row dense>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="movie.title"
                  label="Movie Title *"
                  density="comfortable"
                  :rules="[required]"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="movie.category"
                  :items="CATEGORIES"
                  label="Category *"
                  density="comfortable"
                  :rules="[required]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="movie.original"
                  label="Original Title (Optional)"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="movie.director"
                  label="Director"
                  density="comfortable"
                />
              </v-col>

              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="movie.year"
                  label="Release Year"
                  placeholder="e.g. 2024"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="movie.duration"
                  label="Runtime"
                  placeholder="e.g. 2h 15m"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-select
                  v-model="movie.language"
                  :items="LANGUAGES"
                  label="Language"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-select
                  v-model="movie.quality"
                  :items="QUALITIES"
                  label="Video Quality"
                  density="comfortable"
                />
              </v-col>

              <!-- Star Rating -->
              <v-col cols="12" class="mt-2">
                <div class="d-flex align-center ga-3">
                  <span class="text-caption font-weight-bold text-medium-emphasis">Your Rating:</span>
                  <v-rating
                    v-model="ratingNumber"
                    half-increments
                    density="comfortable"
                    color="amber"
                    active-color="amber"
                  />
                  <span class="text-body-2 font-weight-bold">{{ ratingNumber }} / 5</span>
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Section 2: Synopsis & Media -->
          <v-card class="glass-card pa-6 mb-6">
            <div class="d-flex align-center ga-2 mb-4">
              <v-icon color="secondary" size="22">mdi-text-box-outline</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold mb-0">Synopsis & Trailer</h3>
            </div>

            <v-row dense>
              <v-col cols="12">
                <v-textarea
                  v-model="movie.synopsis"
                  label="Synopsis / Plot Overview"
                  rows="3"
                  auto-grow
                  density="comfortable"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="movie.youtubeId"
                  label="YouTube Trailer Video ID"
                  placeholder="e.g. d9MyW72ELq0"
                  density="comfortable"
                  prepend-inner-icon="mdi-youtube"
                  variant="outlined"
                />
              </v-col>
            </v-row>
          </v-card>

          <!-- Section 3: Cast & Crew -->
          <v-card class="glass-card pa-6 mb-6">
            <div class="d-flex align-center ga-2 mb-4">
              <v-icon color="accent" size="22">mdi-account-multiple-outline</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold mb-0">Cast Members</h3>
            </div>

            <v-text-field
              v-model="movie.casting"
              label="Cast Overview (Comma-separated)"
              density="comfortable"
              class="mb-4"
            />

            <!-- Top 6 Cast Members Details -->
            <div class="text-caption font-weight-bold text-medium-emphasis mb-3">
              Key Actor Avatars (Auto-filled by TMDB or manual)
            </div>
            <v-row dense>
              <v-col
                v-for="n in 6"
                :key="n"
                cols="12"
                sm="6"
                md="4"
              >
                <v-card variant="tonal" class="pa-3 rounded-lg mb-2">
                  <div class="d-flex align-center ga-2 mb-2">
                    <v-avatar size="36" color="primary" class="rounded-lg">
                      <v-img v-if="movie[`actorpix${n}`]" :src="movie[`actorpix${n}`]" cover />
                      <v-icon v-else size="20">mdi-account</v-icon>
                    </v-avatar>
                    <div class="text-caption font-weight-bold">Actor {{ n }}</div>
                  </div>
                  <v-text-field
                    v-model="movie[`actorname${n}`]"
                    :label="`Actor ${n} Name`"
                    density="compact"
                    hide-details
                    class="mb-2"
                  />
                  <v-text-field
                    v-model="movie[`actorpix${n}`]"
                    :label="`Photo URL`"
                    density="compact"
                    hide-details
                  />
                </v-card>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import axios from 'axios'
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { API_KEY, imgTmdbUrl, tmdburl } from '@/config/tmdb'
import { CATEGORIES, LANGUAGES, QUALITIES, categoryFromTmdbGenres } from '@/config/movies'
import MoviesAPI from '@/services/movies'
import { appendMovieFormData, defaultMovieForm, timeConvert } from '@/utils/movieForm'
import { apiErrorMessage } from '@/utils/formValidate'

export default {
  name: 'AddPost',
  setup() {
    const router = useRouter()
    const formRef = ref(null)
    const movie = reactive(defaultMovieForm())
    const imageFile = ref(null)
    const saving = ref(false)
    const savingWishList = ref(false)
    const searchingTmdb = ref(false)
    const error = ref('')
    const autofillMessage = ref('')
    const movieInstance = ref(0)
    const searchResults = ref([])

    const required = (value) => !!value || 'This field is required'

    const ratingNumber = computed({
      get: () => Number(movie.rating) || 0,
      set: (value) => {
        movie.rating = String(value)
      },
    })

    const posterPreview = computed(() => {
      if (movie.imageurl) return movie.imageurl
      const file = imageFile.value
      if (!file) return ''
      const selected = Array.isArray(file) ? file[0] : file
      return selected ? URL.createObjectURL(selected) : ''
    })

    const clearCast = () => {
      for (let n = 1; n <= 6; n += 1) {
        movie[`actorname${n}`] = ''
        movie[`actorpix${n}`] = ''
      }
    }

    const autofill = async () => {
      autofillMessage.value = ''
      error.value = ''
      if (!movie.title || !movie.title.trim()) return

      if (!API_KEY) {
        autofillMessage.value = 'TMDB API key not configured. You can manually enter movie details.'
        return
      }

      searchingTmdb.value = true
      try {
        if (!searchResults.value.length) {
          const { data } = await axios.get(`${tmdburl}search/movie`, {
            params: { api_key: API_KEY, query: movie.title.trim() },
          })
          searchResults.value = data.results || []
          movieInstance.value = 0
        }
        if (!searchResults.value.length) {
          autofillMessage.value = 'No TMDB matches found for this title.'
          return
        }
        await applyTmdbResult(searchResults.value[movieInstance.value])
      } catch (err) {
        error.value = err.message || 'TMDB autofill request failed'
      } finally {
        searchingTmdb.value = false
      }
    }

    const autofillNext = async () => {
      if (!searchResults.value.length) {
        await autofill()
        return
      }
      movieInstance.value = (movieInstance.value + 1) % searchResults.value.length
      await applyTmdbResult(searchResults.value[movieInstance.value])
    }

    const applyTmdbResult = async (result) => {
      if (!result) return
      if (result.title) movie.title = result.title
      movie.year = (result.release_date || '').substring(0, 4)
      movie.imageurl = result.poster_path ? `${imgTmdbUrl}${result.poster_path}` : ''
      movie.synopsis = result.overview || ''
      movie.original = result.original_title || ''

      try {
        const { data: details } = await axios.get(`${tmdburl}movie/${result.id}`, {
          params: { api_key: API_KEY },
        })
        if (details.runtime) {
          movie.duration = timeConvert(details.runtime)
        }
        const mappedCategory = categoryFromTmdbGenres(details.genres)
        if (mappedCategory) {
          movie.category = mappedCategory
        }

        const { data: credits } = await axios.get(`${tmdburl}movie/${result.id}/credits`, {
          params: { api_key: API_KEY },
        })
        const cast = credits.cast || []
        movie.casting = cast.slice(0, 10).map((actor) => actor.name).join(', ')
        clearCast()
        cast.slice(0, 6).forEach((actor, index) => {
          const n = index + 1
          movie[`actorname${n}`] = actor.name
          movie[`actorpix${n}`] = actor.profile_path ? `${imgTmdbUrl}${actor.profile_path}` : ''
        })
        const director = (credits.crew || []).find((person) => person.job === 'Director')
        if (director) movie.director = director.name

        const { data: videos } = await axios.get(`${tmdburl}movie/${result.id}/videos`, {
          params: { api_key: API_KEY },
        })
        const trailer = (videos.results || []).find(
          (video) => video.site === 'YouTube' && video.type === 'Trailer',
        )
        if (trailer) movie.youtubeId = trailer.key

        autofillMessage.value = `Loaded data for "${result.title}" (${movie.year || 'N/A'})${mappedCategory ? ` • ${mappedCategory}` : ''}`
      } catch (err) {
        // partial load
        autofillMessage.value = `Loaded basic info for "${result.title}"`
      }
    }

    const savePost = async (markedValue = '') => {
      error.value = ''
      const title = String(movie.title || '').trim()
      const category = String(movie.category || '').trim()

      if (!title || !category) {
        error.value = 'Title and category are required before saving.'
        return
      }

      movie.title = title
      movie.category = category
      movie.marked = markedValue

      const isWishList = markedValue === '2'
      if (isWishList) {
        savingWishList.value = true
      } else {
        saving.value = true
      }

      try {
        const formData = new FormData()
        appendMovieFormData(formData, movie)
        if (imageFile.value) {
          const file = Array.isArray(imageFile.value) ? imageFile.value[0] : imageFile.value
          if (file) formData.append('image', file)
        }
        await MoviesAPI.addPost(formData)
        const message = isWishList ? 'Movie added to your wish list.' : 'Movie added to collection.'
        router.push({ name: 'MoviesHome', query: { message } })
      } catch (err) {
        error.value = apiErrorMessage(err, 'Failed to save movie')
      } finally {
        saving.value = false
        savingWishList.value = false
      }
    }

    const saveMovie = () => savePost('0')
    const saveWishList = () => savePost('2')

    return {
      formRef,
      movie,
      imageFile,
      saving,
      savingWishList,
      searchingTmdb,
      error,
      autofillMessage,
      searchResults,
      movieInstance,
      CATEGORIES,
      LANGUAGES,
      QUALITIES,
      required,
      ratingNumber,
      posterPreview,
      autofill,
      autofillNext,
      saveMovie,
      saveWishList,
    }
  },
}
</script>

<style scoped>
.add-post-view {
  max-width: 1240px;
  margin: 0 auto;
}

.autofill-card {
  border: 1px solid rgba(99, 102, 241, 0.3) !important;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.06), rgba(236, 72, 153, 0.04)) !important;
}

.sticky-poster-card {
  position: sticky;
  top: 80px;
}

.poster-dropzone {
  position: relative;
  aspect-ratio: 2 / 3;
  border-radius: 12px;
  overflow: hidden;
}

.poster-empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 2px dashed rgba(128, 128, 128, 0.3);
  border-radius: 12px;
  padding: 16px;
  background: rgba(128, 128, 128, 0.05);
}

.border-t-subtle {
  border-top: 1px solid rgba(128, 128, 128, 0.12);
}
</style>

